import { addDays, addSeconds, format } from 'date-fns'
import { SelectOption, Employee } from '../../../services/dashboardService'
import { BookingBookingPayload, BookingEmployeeBooking } from '../../../services/booking/types'
import { Time } from '../Shared/enums'
import { BookingPeriods } from '../Popover/enums'

export const getJson = (additionalInfo: string) => {
  try {
    return JSON.parse(additionalInfo)
  } catch (e) {
    return {}
  }
}

export const getDeskName = (additionalInfo: string, label: string) => {
  if (!additionalInfo) {
    return label
  }
  let json: { desc: { deskDesc: string } } | null = null
  json = getJson(additionalInfo)
  return `${json?.desc?.deskDesc ? `${json.desc.deskDesc.toUpperCase()}` : ''}${label}`
}

export const getUserInitials = (displayName: string) => {
  const splt = displayName.split(' ')
  return splt.map(m => m.substring(0, 1))
}

export const dateToNumber = (date: Date | string): number => {
  if (Object.prototype.toString.call(date) === '[object Date]') {
    return Number(format(date as Date, 'yyyyMMdd'))
  }
  return Number(format(new Date(date), 'yyyyMMdd'))
}

export const extractEmployees = (allEmployees: Employee[]): SelectOption[] =>
  allEmployees.map(e => ({ value: e.employeeId, displayValue: e.employeeName }))

export const cleanUsername = (username: string | undefined) => username?.replace(/ /g, '') || ''

export const removeOneSecond = (date: Date) => addSeconds(date, -1)

export const combineDateAndTime = (date: Date | string, time: Date | string) => {
  const newDate = new Date(date)

  if (typeof time === 'string') {
    const [hours, minutes, seconds] = time.split(':')
    newDate.setHours(parseInt(hours, 10))
    newDate.setMinutes(parseInt(minutes, 10))
    newDate.setSeconds(seconds ? parseInt(seconds, 10) : 0)
  } else {
    newDate.setHours(time.getHours())
    newDate.setMinutes(time.getMinutes())
    newDate.setSeconds(time.getSeconds())
  }
  return newDate
}
export const getDateRangeFromDateAndTimes = (startDate: Date | string, timeFrom: Date | string, timeTo: Date | string) => {
  const start = combineDateAndTime(startDate, timeFrom)
  const end = combineDateAndTime(timeFrom < timeTo ? startDate : addDays(new Date(startDate), 1), timeTo)
  return { start, end }
}

export const doesBookingOverlapWithPeriod = (
  booking: BookingBookingPayload, floorplanViewingDate: Date, fromTime: Date | string, toTime: Date | string
) => {
  const { start: bookingStart, end: bookingEnd } = getDateRangeFromDateAndTimes(booking.fromDate, booking.fromTime, booking.toTime)
  const { start: periodStartDate, end: periodEndDate } = getDateRangeFromDateAndTimes(floorplanViewingDate, fromTime, toTime)
  return (bookingStart <= periodEndDate && bookingEnd >= periodStartDate)
}

export const getPeriodFromBooking = (booking: BookingBookingPayload | BookingEmployeeBooking) => {
    const fullDateFrom = combineDateAndTime(booking.fromDate, booking.fromTime)
    const fullDateTo = combineDateAndTime(booking.toDate, booking.toTime)
    if (format(fullDateFrom, 'HH:mm:ss') === Time.MIDNIGHT && format(fullDateTo, 'HH:mm:ss') === Time.MIDDAY_MINUS_ONE_SECOND) {
      return BookingPeriods.HALF_DAY_FIRST_HALF
    }
    if (format(fullDateFrom, 'HH:mm:ss') === Time.MIDDAY && format(fullDateTo, 'HH:mm:ss') === Time.MIDNIGHT_MINUS_ONE_SECOND) {
      return BookingPeriods.HALF_DAY_SECOND_HALF
    }
    if (format(fullDateFrom, 'HH:mm:ss') === Time.MIDNIGHT && format(fullDateTo, 'HH:mm:ss') === Time.MIDNIGHT_MINUS_ONE_SECOND) {
      return BookingPeriods.FULL_DAY
    }
    return BookingPeriods.CUSTOM_TIME_SLOT
}

/**
 * Checks if a user has any bookings that overlap with the specified time period for the same date.
 * @param employeeId The employee ID to check bookings for
 * @param date The date to check (as Date object or string)
 * @param fromTime The start time to check (HH:mm:ss format or Date object)
 * @param toTime The end time to check (HH:mm:ss format or Date object)
 * @param existingBookings Array of existing bookings to check against
 * @returns true if there's an overlapping booking, false otherwise
 */
export const hasOverlappingBookingForEmployee = (
  employeeId: number,
  date: Date | string,
  fromTime: Date | string,
  toTime: Date | string,
  existingBookings: BookingBookingPayload[]
): boolean => {
  const checkDate = format(new Date(date), 'yyyy-MM-dd')
  
  const userBookingsOnDate = existingBookings.filter(booking => 
    booking.employeeId === employeeId && 
    booking.fromDate === checkDate
  )
  
  if (userBookingsOnDate.length === 0) {
    return false
  }
  
  return userBookingsOnDate.some(booking => 
    doesBookingOverlapWithPeriod(booking, new Date(date), fromTime, toTime)
  )
}
